/**
 * Decks, seen by the parent.
 *
 * Both apps show the same three things about her cards: what today holds
 * (one Start that plays every deck, with each deck's due and new counts),
 * her decks and how many cards she knows in each, and what she knows across
 * everything (letters, sounds and words by standing). The arithmetic lives
 * here once, on top of the shared scheduler, so the web and the phone can
 * never disagree about a count, and today's numbers are the plan the cards
 * engine will actually play.
 */

import { cardItem, deckLadder, playingDeck, validateDeck, type Card, type Deck } from './decks'
import { engineMaterialHtml } from './engines'
import { decodableLevel, GRAPHEMES, LEVEL_HOLD, PHONEME_BY_ID, SIGHT_WORDS, SPOKEN_WORDS, splitGraphemes, WORDS } from './phonics'
import { PICTURES } from './pictures.generated'
import { starterDeck, TEAM_SETS } from './starterDecks'
import { LITTLE_BOOKS, SIGHT_SETS, SOUND_SETS } from './books'
import type { MaterialAsset } from './index'
import { CARD_MS, holds, itemKey, parseItemKey, planSession, retrievability, shareDay, standing, type ItemMemory, type SessionCard, type SetGate, type SetOpens, type Standing } from '../memory'
import { addDays, diffDays, fromCalendarDate, toCalendarDate } from '../schedule'

// ─────────────────────────────────────────────────────────────
// Standing, in the parent's words
// ─────────────────────────────────────────────────────────────

/** Weakest to strongest: the order every legend and list uses. */
export const STANDINGS: Standing[] = ['new', 'learning', 'shaky', 'known', 'mastered']

/** Plain words for a parent. Growth only: nothing here says "behind". */
export const STANDING_LABEL: Record<Standing, string> = {
  new: 'Not met yet',
  learning: 'Learning',
  shaky: 'Wobbly',
  known: 'Knows it',
  mastered: 'Mastered',
}

const RANK: Record<Standing, number> = { new: 0, learning: 1, shaky: 2, known: 3, mastered: 4 }

// ─────────────────────────────────────────────────────────────
// Deck artifacts
// ─────────────────────────────────────────────────────────────

/** The fields of a stored deck artifact these helpers read. Both apps' rows fit. */
export interface DeckArtifact {
  _id: string
  kind?: string
  title?: string
  childId?: string
  data?: unknown
  carry?: unknown
  assets?: MaterialAsset[]
  generation?: { status?: string; error?: string; prompt?: string }
  createdAt?: number
  _creationTime?: number
  deleted?: boolean
}

/** A deck artifact's deck when it is whole enough to play; null while it is being made or if it came back broken. */
export function readDeck(a: DeckArtifact | undefined): Deck | null {
  if (!a || a.generation?.status === 'generating' || a.generation?.status === 'failed') return null
  return validateDeck(a.data).length ? null : (a.data as Deck)
}

/** Deck artifacts, oldest first: the order decks play in and the order they were started. */
export function deckArtifacts<T extends DeckArtifact>(rows: T[]): T[] {
  const born = (a: T) => a.createdAt ?? a._creationTime ?? 0
  return rows.filter((a) => a.kind === 'deck' && !a.deleted).sort((a, b) => born(a) - born(b))
}

// ─────────────────────────────────────────────────────────────
// Today
// ─────────────────────────────────────────────────────────────

export interface DeckDay {
  /** What today's session will play from this deck, in order: the cards engine's own plan. */
  plan: SessionCard[]
  due: number
  fresh: number
  practice: number
  /** Cards of this deck she has already answered today. */
  doneToday: number
  /** How many cards today's session holds for this deck, counting the ones she already did. */
  target: number
  /** She did today's cards for this deck, not just a few of them. */
  finished: boolean
  /**
   * Nothing from this deck today: the day's budget went to the other decks
   * (shareDay), or it has nothing due and nothing new she is ready for.
   */
  waiting: boolean
  /** Its cards she has ever met, and those she met for the first time today. */
  met: number
  newToday: number
  /** Cards in the deck, and how many at each standing. */
  total: number
  standings: Record<Standing, number>
}

/** What the cards engine keeps between sessions (runtime/engines/cards.js snapshot). */
interface CardsCarry {
  day?: string
  fresh?: Record<string, number>
  acc?: Record<string, number>
}

function cardsCarry(carry: unknown): CardsCarry {
  return carry && typeof carry === 'object' ? (carry as CardsCarry) : {}
}

const clamp = (n: unknown, lo: number, hi: number) => {
  const v = Math.round(Number(n))
  return Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo))
}

/**
 * One deck's day, planned exactly as the cards engine plans it (planDeck in
 * runtime/engines/cards.js): the same size and new-card limits, the new-card
 * gate from the accuracy the engine saved in its carry, today's new cards
 * from the carry too, one card when nothing else qualifies, and each skill
 * (generator card) coming round once more while the deck has room. The
 * engine is the one source for these inputs, so the counts here are the
 * cards she will see. `today` is her calendar date and `dayStart` when it
 * began; `gate` is sightGate over her decks, which the engine gets too.
 */
export function deckDay(deck: Deck, memories: Record<string, ItemMemory>, now: number, day: { dayStart: number; today: string; carry?: unknown; words?: Map<string, string[]>; sounds?: Record<string, string[]>; gate?: SetGate | SetGate[] }): DeckDay {
  const ladder = [...new Set(deckLadder(deck).map((k) => k.toLowerCase()))]
  const carry = cardsCarry(day.carry)
  const size = clamp(deck.daily || 10, 3, 20)
  const maxNew = clamp(deck.maxNew ?? 4, 0, 10)
  // A deck she has never met has no accuracy yet and starts as if all were right, as the engine does.
  const saved = carry.acc?.[deck.id]
  const acc = typeof saved === 'number' ? saved : ladder.some((k) => (memories[k]?.seen ?? 0) > 0) ? undefined : 1
  const newToday = carry.day === day.today ? carry.fresh?.[deck.id] ?? 0 : 0
  // Resting cards sit out today's plan, as the session the engine plays leaves them out; they still count as hers.
  const playable = [...new Set(deckLadder(playingDeck(deck, now)).map((k) => k.toLowerCase()))]
  let picked = playable.length ? planSession({ ladder: playable, memories, now, size, maxNew, recentAccuracy: acc, newToday, gate: day.gate, fillWithKnown: false }) : []
  if (!picked.length && playable.length) picked = planSession({ ladder: playable, memories, now, size, maxNew: 1, fillWithKnown: false })
  const kindOf = new Map(deck.cards.map((c) => [cardItem(deck.id, c.id).toLowerCase(), c.kind]))
  const plan = [...picked]
  const gens = picked.filter((c) => kindOf.get(c.item) === 'generator')
  while (plan.length < size && gens.length) {
    const g = gens.splice(gens.length > 1 && plan[plan.length - 1]!.item === gens[0]!.item ? 1 : 0, 1)[0]!
    plan.push({ item: g.item, why: 'practice' })
  }

  // A card that is one word stands where the word stands, wherever she read
  // it (a game, a book, another deck), the way What she knows and the
  // reading path count it; `words` is wordItems over her memory and decks.
  // A card that teaches a sound stands where the sound stands, by the
  // evidence the path's Sounds track reads (`sounds`, soundEvidence): the
  // sound itself, the words with it in she reads well, and every card of it.
  const wordOf = new Map(deck.cards.map((c) => [cardItem(deck.id, c.id).toLowerCase(), c.front.text?.trim().toLowerCase()]))
  const soundOf = day.sounds ? soundCards([deck]) : {}
  const standings = Object.fromEntries(STANDINGS.map((s) => [s, 0])) as Record<Standing, number>
  for (const k of ladder) {
    const w = wordOf.get(k)
    const also = w && day.words ? day.words.get(w) ?? [] : []
    const sound = soundOf[k] ? soundProof(day.sounds!, soundOf[k]!, memories, now) : []
    standings[strongest([k, ...also, ...sound], memories, now)]++
  }
  const fresh = plan.filter((c) => c.why === 'new').length
  const doneToday = ladder.filter((k) => (memories[k]?.last ?? 0) >= day.dayStart).length
  // Today's session holds every card she has met (reviews and practice) plus
  // the new ones it still has room for, up to the deck's size. Answering one
  // card is not the day done; answering that many is.
  const seen = ladder.filter((k) => memories[k]?.reps).length
  const target = Math.max(1, Math.min(size, seen + fresh))
  return {
    plan,
    due: plan.filter((c) => c.why === 'due').length,
    fresh,
    practice: plan.filter((c) => c.why === 'practice').length,
    doneToday,
    target,
    finished: doneToday > 0 && doneToday >= target,
    waiting: false,
    met: seen,
    newToday,
    total: ladder.length,
    standings,
  }
}

/**
 * A deck's day once every deck has shared the day's budget (shareDay): its
 * part of today's session, and done when she has answered that many.
 */
function sharedDay(day: DeckDay, plan: SessionCard[]): DeckDay {
  const target = Math.min(day.target, plan.length)
  return {
    ...day,
    plan,
    due: plan.filter((c) => c.why === 'due').length,
    fresh: plan.filter((c) => c.why === 'new').length,
    practice: plan.filter((c) => c.why === 'practice').length,
    target,
    finished: day.doneToday > 0 && day.doneToday >= target,
    waiting: !plan.length && !day.doneToday,
  }
}

/** Cards she holds now: known or mastered. */
export function knownCount(day: Pick<DeckDay, 'standings'>): number {
  return day.standings.known + day.standings.mastered
}

/**
 * Today's session, ready to launch: every deck that is not resting, in the
 * order they were started, as one cards-engine material carrying every
 * deck's media. Pictures are named per deck and card; a recorded line is
 * named by its language and text (lineId), so two decks saying the same
 * words in the same language share one id and one copy is sent (the host
 * keys assets by id): a ready one over one still coming or failed.
 * `anchor` is the artifact the play is logged against: the oldest deck, so
 * what the session saves for next time (its carry) always lives in one place.
 */
export function cardSession(rows: DeckArtifact[], now = Date.now()): { html: string; assets: MaterialAsset[]; anchor: DeckArtifact; decks: Deck[] } | null {
  const ready = deckArtifacts(rows).map((a) => ({ a, deck: readDeck(a) })).filter((x): x is { a: DeckArtifact; deck: Deck } => !!x.deck)
  // A deck plays without its resting cards, and a deck with every card resting sits out like a paused one.
  const playing = ready.filter((x) => !x.deck.paused).map((x) => ({ ...x, deck: playingDeck(x.deck, now) })).filter((x) => x.deck.cards.length)
  if (!playing.length) return null
  const decks = playing.map((x) => x.deck)
  const byId = new Map<string, MaterialAsset>()
  for (const x of playing.flatMap((p) => p.a.assets ?? [])) {
    const had = byId.get(x.id)
    if (!had || (had.status !== 'ready' && x.status === 'ready')) byId.set(x.id, x)
  }
  const assets = [...byId.values()]
  const gate = deckGates(ready.map((x) => x.deck))
  return { html: engineMaterialHtml('cards', gate ? { decks, gate } : { decks }), assets, anchor: ready[0]!.a, decks }
}

// ─────────────────────────────────────────────────────────────
// The streak
// ─────────────────────────────────────────────────────────────

/**
 * The days she did her cards, as calendar dates in her time zone. Two
 * sources, because neither alone is complete: the session's carry (the cards
 * engine keeps the dates it ran) and her memory (a card last answered on a
 * day proves that day happened, though a card answered again later hides its
 * earlier day).
 */
export function cardDays(memories: Record<string, ItemMemory>, carry: unknown, tz: string): Set<string> {
  const days = new Set<string>()
  const add = (v: unknown) => {
    if (typeof v === 'number' && v > 0) days.add(toCalendarDate(v, tz))
    else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) days.add(v.slice(0, 10))
  }
  const c = carry && typeof carry === 'object' ? (carry as Record<string, unknown>) : {}
  if (Array.isArray(c.days)) c.days.forEach(add)
  add(c.last)
  add(c.lastDate)
  for (const m of Object.values(memories)) if (m.item.startsWith('card:') && m.reps > 0) add(m.last)
  return days
}

/**
 * Days in a row, the last week as dots, and the last day she did any. A streak is still alive on a day
 * she has not played yet: it counts through yesterday until today is over.
 */
export function cardStreak(days: Set<string>, today: string): { streak: number; doneToday: boolean; week: Array<{ date: string; done: boolean }>; last?: string } {
  const doneToday = days.has(today)
  let streak = 0
  for (let d = doneToday ? today : addDays(today, -1); days.has(d); d = addDays(d, -1)) streak++
  const week = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6)).map((date) => ({ date, done: days.has(date) }))
  const last = [...days].filter((d) => d <= today).sort().at(-1)
  return { streak, doneToday, week, last }
}

// ─────────────────────────────────────────────────────────────
// The whole page
// ─────────────────────────────────────────────────────────────

export interface DeckRow<T extends DeckArtifact = DeckArtifact> {
  artifact: T
  /** Null while it is being made, or when it came back unplayable. */
  deck: Deck | null
  status: 'ready' | 'generating' | 'failed'
  day: DeckDay | null
}

/**
 * Everything the cards page shows, from her deck artifacts and memory: each
 * deck with today's plan, the session to launch, the streak, and whether
 * today is done. Both apps render this; neither computes any of it.
 */
export function cardsOverview<T extends DeckArtifact>(rows: T[], memory: Record<string, ItemMemory>, now: number, tz: string) {
  const today = toCalendarDate(now, tz)
  const dayStart = fromCalendarDate(today, tz)
  const decks: DeckRow<T>[] = deckArtifacts(rows).map((artifact) => {
    const deck = readDeck(artifact)
    const status = artifact.generation?.status === 'generating' ? 'generating' : deck ? 'ready' : 'failed'
    return { artifact, deck, status, day: null as DeckDay | null }
  })
  const session = cardSession(rows, now)
  const all = decks.flatMap((d) => (d.deck ? [d.deck] : []))
  const words = wordItems(memory, all)
  const sounds = soundEvidence(all, memory)
  const gate = deckGates(all, memory)
  // The engine keeps its carry on the session's anchor; every deck's day reads it there, as the engine does.
  for (const d of decks) if (d.deck) d.day = deckDay(d.deck, memory, now, { dayStart, today, carry: session?.anchor.carry, words, sounds, gate })
  // Every deck that is not resting shares one day's budget, as the engine shares it.
  const playing = decks.filter((d) => d.deck && !d.deck.paused && d.day)
  const shared = shareDay(playing.map((d) => d.day!))
  playing.forEach((d, i) => { d.day = sharedDay(d.day!, shared[i]!) })
  // Today's session: the decks with cards today, or done today.
  const active = playing.filter((d) => !d.day!.waiting)
  const toPlay = active.reduce((n, d) => n + (d.day?.plan.length ?? 0), 0)
  return {
    decks,
    active,
    session,
    streak: cardStreak(cardDays(memory, session?.anchor.carry, tz), today),
    toPlay,
    /** About how long today's cards take her, in whole minutes. */
    minutes: Math.max(1, Math.round((toPlay * CARD_MS) / 60_000)),
    doneToday: active.length > 0 && active.every((d) => d.day?.finished),
    /** Some of today's cards are done and some are not: she stopped partway. */
    partway: active.some((d) => (d.day?.doneToday ?? 0) > 0) && !active.every((d) => d.day?.finished),
    playable: decks.flatMap((d) => (d.deck ? [d.deck] : [])),
  }
}

// ─────────────────────────────────────────────────────────────
// What she knows
// ─────────────────────────────────────────────────────────────

/** The multi-letter spellings worth their own tile: digraphs and the long vowels. */
export const DIGRAPHS = ['sh', 'ch', 'th', 'ng', 'qu', 'ck', 'ee', 'oo', 'ai', 'oa', 'ar', 'or', 'igh', 'ow']

export interface KnowledgeTile {
  /** The letter or spelling, lowercase. */
  key: string
  /** What the tile shows: "Aa", "sh". */
  label: string
  /** The strongest standing among its own items: what she can do with it at her best. */
  standing: Standing
  /** What the sheet shows when it is opened: its own items, then the sound it makes if she has met it. */
  items: string[]
  /**
   * How she stands on the sound this spelling makes, from the same sound:*
   * item the reading path reads, kept apart from the letter's own standing:
   * knowing the letter M and knowing "mmm" are two things, and a page that
   * shows one colour for both says two things at once.
   */
  sound?: Standing
}

const GLYPHS = new Set(['abcdefghijklmnopqrstuvwxyz'.split(''), DIGRAPHS].flat())

/**
 * A deck about letters: most of its cards show one letter or spelling
 * (capitals, lowercase, sounds, or one made from a sentence). A word deck
 * that happens to hold "a" or "I" is not one, so the sight word "a" never
 * speaks for the letter A.
 */
function letterDeck(d: Deck): boolean {
  const faces = d.cards.map((c) => c.front.text?.trim().toLowerCase()).filter((t): t is string => !!t)
  return faces.length > 0 && faces.filter((t) => GLYPHS.has(t)).length * 2 > faces.length
}

function strongest(items: string[], memories: Record<string, ItemMemory>, now: number): Standing {
  let best: Standing = 'new'
  for (const k of items) {
    const s = standing(memories[k], now)
    if (RANK[s] > RANK[best]) best = s
  }
  return best
}

/**
 * The letter and sound grid: A to Z, then the digraphs. A tile wears the best
 * of what is about that spelling: the letter item and cards in letter decks
 * that show it. The line under it is the sound, read from the same evidence
 * the path's Sounds track and her sound cards count (soundEvidence): the
 * sound itself, every card of it, and the words with it in she reads well.
 * A letter card that speaks its sound is a sound card, so its tile stands
 * where the sound does, as the deck's own count has it. One rule, so the
 * grid, the path and the deck tiles name the same letters.
 */
export function knowledgeGrid(memories: Record<string, ItemMemory>, decks: Deck[], now: number): { letters: KnowledgeTile[]; digraphs: KnowledgeTile[] } {
  const letterDecks = decks.filter(letterDeck)
  const sounds = soundEvidence(decks, memories)
  const soundCardItems = soundCards(decks)
  const tile = (key: string, label: string): KnowledgeTile => {
    const own = [`letter:${key}`]
    for (const d of letterDecks) for (const c of d.cards) if (c.front.text?.trim().toLowerCase() === key) own.push(cardItem(d.id, c.id))
    const heard = GRAPHEMES[key] || sounds[key] ? soundProof(sounds, key, memories, now) : undefined
    const speaksSound = own.some((k) => soundCardItems[k.toLowerCase()] === key)
    const met = own.filter((k) => memories[k]?.reps)
    // The sheet shows the tile's own items, then whatever shows the sound: the sound itself and the words that prove it.
    const proof = (heard ?? []).filter((k) => !own.includes(k) && memories[k]?.reps)
    return {
      key,
      label,
      standing: strongest(speaksSound && heard ? [...own, ...heard] : own, memories, now),
      items: [...(met.length ? met : own.slice(0, 2)), ...proof],
      ...(heard ? { sound: strongest(heard, memories, now) } : {}),
    }
  }
  const letters = 'abcdefghijklmnopqrstuvwxyz'.split('').map((l) => tile(l, l.toUpperCase() + l))
  return { letters, digraphs: DIGRAPHS.map((g) => tile(g, g)) }
}

export interface KnownWord {
  word: string
  standing: Standing
  items: string[]
}

/**
 * Every item she has met that is about one word, by the word (lowercase):
 * decoded words, sight words, and cards whose front is a single word. A
 * one-letter card is a letter, never the sight word "a" or "I". What she
 * knows and the reading path's sight words both read a word through this,
 * so the two never disagree about it.
 */
export function wordItems(memories: Record<string, ItemMemory>, decks: Deck[]): Map<string, string[]> {
  const byWord = new Map<string, string[]>()
  const push = (w: string, k: string) => byWord.set(w, [...(byWord.get(w) ?? []), k])
  for (const k of Object.keys(memories)) {
    const p = parseItemKey(k)
    if (p && (p.kind === 'word' || p.kind === 'sight') && memories[k]!.reps > 0) push(p.id, k)
  }
  for (const d of decks) for (const c of d.cards) {
    const text = cardWord(c)
    const k = cardItem(d.id, c.id)
    if (text && memories[k]?.reps) push(text, k)
  }
  return byWord
}

/** The word a card shows, when it shows one word: a one-letter card is a letter, never the word "a" or "I". */
function cardWord(c: Card): string | null {
  const text = c.front.text?.trim().toLowerCase()
  return text && /^[\p{L}']{2,}$/u.test(text) ? text : null
}

/**
 * The reading path's sight sets as a gate on her decks: a card showing a
 * sight word comes in as new only once the path has opened that word's
 * set, so the path teaches and the decks review. A word counts as known
 * wherever she read it, as on the path. Undefined when no deck has one.
 */
export function sightGate(decks: Deck[]): SetGate | undefined {
  const sets = SIGHT_SETS.map((set) => set.map((w) => w.toLowerCase()))
  const member = new Set(sets.flat())
  const evidence: Record<string, string[]> = Object.fromEntries([...member].map((w) => [w, [wordItemKey(w)]]))
  const items: Record<string, string> = {}
  for (const d of decks) {
    const sight = d.cards.filter((c) => member.has(c.front.text?.trim().toLowerCase() ?? ''))
    // In a deck of sight words, "a" and "I" are words too; elsewhere a one-letter card is a letter.
    const wordsDeck = sight.length * 2 >= d.cards.length
    for (const c of sight) {
      const w = cardWord(c) ?? (wordsDeck ? c.front.text!.trim().toLowerCase() : null)
      if (!w) continue
      const k = cardItem(d.id, c.id).toLowerCase()
      items[k] = w
      evidence[w]!.push(k)
    }
  }
  return Object.keys(items).length ? { sets, evidence, items } : undefined
}

const SIGHT_SET = new Set(SIGHT_WORDS.map((w) => w.toLowerCase()))

/**
 * Every sound her decks teach, in sets in teaching order: the reading path's
 * letter sets, then the letters no set has yet (k, x, y, q), then the
 * two-letter sounds. Each set opens once she knows half the one before.
 */
const SOUND_CHAIN: string[][] = (() => {
  const sets = SOUND_SETS.map((s) => s.letters)
  const rest = 'abcdefghijklmnopqrstuvwxyz'.split('').filter((l) => !sets.flat().includes(l))
  return [...sets, rest, ...TEAM_SETS]
})()

/**
 * Each sound set's own condition, as data the cards engine can ask of her
 * memory too: a set for blending level n opens once she reads well the
 * words that hold level n - 1 (the bar blendLevelStanding counts), so a set
 * for level 1 is open from the start and the level 2 sounds open the day
 * she holds level 1. The words are every word she can meet in the app (the
 * word bank and the little books). The letters no set has and the
 * two-letter sounds follow the set before them.
 */
export const SOUND_OPENS: Array<SetOpens | null> = (() => {
  const texts = [...WORDS.map((w) => w.word), ...LITTLE_BOOKS.flatMap((b) => [b.config.title, ...b.config.pages.map((p) => p.text)].join(' ').toLowerCase().split(/[^a-z]+/))]
  const levels = new Map<string, number>()
  for (const w of new Set(texts)) {
    const level = w && !SIGHT_SET.has(w) ? decodableLevel(w) : null
    if (level) levels.set(w, level)
  }
  return SOUND_CHAIN.map((_, i) => {
    const set = SOUND_SETS[i]
    if (!set) return null
    const from = set.level - 1
    return { items: from < 1 ? [] : [...levels].filter(([, l]) => l >= from).map(([w]) => itemKey('word', w)), need: from < 1 ? 0 : LEVEL_HOLD }
  })
})()

/**
 * Every memory item that shows she has a sound, by its letter or letters:
 * the sound itself (when no other spelling shares it, so ck waits on ck),
 * each word-bank word with it in (reading cat proves k, a and t), and every
 * card that teaches it. The reading path's Sounds track and her decks count
 * by this one list.
 */
export function soundEvidence(decks: Deck[], memories: Record<string, ItemMemory> = {}): Record<string, string[]> {
  const members = SOUND_CHAIN.flat()
  // The words she can sound out: the word bank, and any other she has read.
  const words = new Map<string, string[]>(WORDS.map((w) => [w.word, w.g]))
  for (const k of Object.keys(memories)) {
    const p = parseItemKey(k)
    const g = p?.kind === 'word' && !SIGHT_SET.has(p.id) && !words.has(p.id) ? splitGraphemes(p.id) : null
    if (g) words.set(p!.id, g)
  }
  const out: Record<string, string[]> = {}
  for (const m of members) {
    const id = GRAPHEMES[m]
    out[m] = [
      ...(id && (m.length === 1 || id === m) ? [itemKey('sound', id)] : []),
      ...[...words].filter(([, g]) => g.includes(m)).map(([w]) => itemKey('word', w)),
    ]
  }
  for (const [k, m] of Object.entries(soundCards(decks))) out[m]!.push(k)
  return out
}

/**
 * What shows she has a sound, from soundEvidence: the sound and its cards as
 * they stand, and a word only once she reads it well (reading cat proves k, a
 * and t; a word she has only met proves nothing yet).
 */
function soundProof(sounds: Record<string, string[]>, spelling: string, memories: Record<string, ItemMemory>, now: number): string[] {
  return (sounds[spelling] ?? []).filter((k) => !k.startsWith('word:') || holds(memories[k], now))
}

/** Cards that teach one sound: a letter or two-letter sound on the front, spoken as its sound. */
function soundCards(decks: Deck[]): Record<string, string> {
  const members = new Set(SOUND_CHAIN.flat())
  const out: Record<string, string> = {}
  for (const d of decks) for (const c of d.cards) {
    const text = c.front.text?.trim().toLowerCase() ?? ''
    if (members.has(text) && c.front.say?.startsWith('p:')) out[cardItem(d.id, c.id).toLowerCase()] = text
  }
  return out
}

/**
 * The reading path's sound sets, then the two-letter sounds, as a gate on
 * her decks: a card teaching a sound comes in as new only once its set is
 * open, so the cards never run ahead of the sounds she is on. Undefined
 * when no deck has one.
 */
export function soundGate(decks: Deck[], memories?: Record<string, ItemMemory>): SetGate | undefined {
  const items = soundCards(decks)
  if (!Object.keys(items).length) return undefined
  return { sets: SOUND_CHAIN, evidence: soundEvidence(decks, memories), items, opens: SOUND_OPENS }
}

/**
 * A deck's own steps as a gate (Deck.steps): each group of cards opens once
 * she knows half the group before it, the rule the path's sets follow. A
 * deck installed from a starter before the starter had steps climbs by the
 * starter's. A card is shown by itself, and a counting or sum card also by
 * the same skill in her games (skillEvidence), so sums she does in games
 * open the sums on her cards.
 */
export function stepGates(decks: Deck[]): SetGate[] {
  return decks.flatMap((d) => {
    const steps = d.steps ?? starterDeck(d.id)?.steps
    const byId = new Map(d.cards.map((c) => [c.id, c]))
    const sets = (steps ?? []).map((group) => group.filter((id) => byId.has(id)).map((id) => cardItem(d.id, id).toLowerCase())).filter((g) => g.length)
    if (sets.length < 2) return []
    const cardOf = new Map(d.cards.map((c) => [cardItem(d.id, c.id).toLowerCase(), c]))
    const members = sets.flat()
    return [{ sets, evidence: Object.fromEntries(members.map((k) => [k, [k, ...skillEvidence(cardOf.get(k)!)]])), items: Object.fromEntries(members.map((k) => [k, k])) }]
  })
}

/**
 * The fact keys a game records for the skill a counting or sum card
 * teaches (MATERIAL_CONTRACT: fact:count-7, fact:add-3-2, fact:subtract-4-1).
 * Counting to N is shown by counting, adding up to or taking away from a
 * number in the top half of its range (to 10: six to ten); adding to N by a
 * sum in that range, and taking away from N by taking from a number in it.
 * Easy facts never show a harder card: 1 + 1 says nothing about counting to 10.
 */
export function skillEvidence(card: Card): string[] {
  const type = card.kind === 'generator' ? card.gen?.type : undefined
  const max = Number(card.gen?.params?.max)
  if (!(type === 'count' || type === 'add' || type === 'subtract') || !(max > 0)) return []
  const top = Array.from({ length: max }, (_, i) => i + 1).filter((n) => n > max / 2)
  const sums = (n: number) => Array.from({ length: n + 1 }, (_, a) => `fact:add-${a}-${n - a}`)
  const takes = (n: number) => Array.from({ length: n + 1 }, (_, b) => `fact:subtract-${n}-${b}`)
  return top.flatMap((n) => (type === 'add' ? sums(n) : type === 'subtract' ? takes(n) : [`fact:count-${n}`, ...sums(n), ...takes(n)]))
}

/**
 * The words on her cards that she sounds out, as one set that is always
 * open: it holds nothing back, and says which word a card shows, so a word
 * she already reads in her books and games is not new to her on a card.
 */
export function wordGate(decks: Deck[]): SetGate | undefined {
  const items: Record<string, string> = {}
  for (const d of decks) for (const c of d.cards) {
    const w = c.kind === 'read' ? cardWord(c) : null
    if (w && !SIGHT_SET.has(w)) items[cardItem(d.id, c.id).toLowerCase()] = w
  }
  const words = [...new Set(Object.values(items))]
  return words.length ? { sets: [words], evidence: Object.fromEntries(words.map((w) => [w, [itemKey('word', w)]])), items } : undefined
}

/** Every gate on her decks: sight words and sounds, each opening by the path's own rule, the words she reads, and each deck's own steps. */
export function deckGates(decks: Deck[], memories?: Record<string, ItemMemory>): SetGate[] | undefined {
  const gates = [sightGate(decks), soundGate(decks, memories), wordGate(decks), ...stepGates(decks)].filter((g): g is SetGate => !!g)
  return gates.length ? gates : undefined
}

/** The one memory item a word is read under: sight:<w> for a sight word, word:<w> for one she sounds out. */
export function wordItemKey(word: string): string {
  const w = word.trim().toLowerCase()
  return itemKey(SIGHT_SET.has(w) ? 'sight' : 'word', w)
}

/**
 * An item key as memory keeps it now. A word recorded as a sight word that
 * the list no longer calls one (in, on, can: she sounds them out) is the
 * word she reads, so an older app or an older row never splits one word
 * into two memories. Every other key is already its own.
 */
export function canonicalItem(item: string): string {
  const p = parseItemKey(item)
  return p && p.kind === 'sight' ? wordItemKey(p.id) : item
}

/**
 * The item a memory is told under, to a parent or the agent: a card that
 * shows one of the app's words is that word, the way the path and her deck
 * tiles count it (in and on are words she sounds out, whichever deck once
 * taught them as sight words); a card no longer in any of her decks is
 * history and not told; anything else is its own key, canonical.
 */
export function toldItem(key: string, decks: Deck[]): string | null {
  const item = canonicalItem(key)
  const p = parseItemKey(item)
  if (p?.kind !== 'card') return item
  const [deckId, cardId] = p.id.split('/')
  const card = decks.find((d) => d.id === deckId)?.cards.find((c) => c.id === cardId)
  if (!card) return null
  const w = cardWord(card)
  return w && APP_WORDS.has(w) ? wordItemKey(w) : item
}

/** Every word the app reads with her: the word bank, the sight words and the everyday words. */
const APP_WORDS = new Set([...WORDS.map((w) => w.word), ...SPOKEN_WORDS.map((w) => w.toLowerCase())])

/**
 * Every item a parent's word about a word reaches: the word as she reads it
 * in games and books (a sight word or a word to sound out) and each card in
 * her decks that shows it, so the path, the decks and What she knows all
 * agree after "she knows is".
 */
export function wordMarkItems(word: string, decks: Deck[]): string[] {
  const w = word.trim().toLowerCase()
  const out = [wordItemKey(w)]
  for (const d of decks) for (const c of d.cards) if (c.front.text?.trim().toLowerCase() === w) out.push(cardItem(d.id, c.id))
  return out
}

/**
 * Words she has met, strongest first within each standing. One entry per
 * word, wearing its best standing.
 */
export function wordsByStanding(memories: Record<string, ItemMemory>, decks: Deck[], now: number): Record<Standing, KnownWord[]> {
  const byWord = wordItems(memories, decks)
  const out = Object.fromEntries(STANDINGS.map((s) => [s, [] as KnownWord[]])) as Record<Standing, KnownWord[]>
  for (const [word, items] of byWord) {
    const s = strongest(items, memories, now)
    out[s].push({ word, standing: s, items })
  }
  for (const s of STANDINGS) {
    out[s].sort((a, b) => best(b.items) - best(a.items) || a.word.localeCompare(b.word))
  }
  return out

  function best(items: string[]) {
    return Math.max(...items.map((k) => (memories[k] ? retrievability(memories[k]!, now) : 0)))
  }
}

/** One item in a sentence a parent reads: `The sound "sh" (as in ship)`, `"cat"`, `"A" in Capitals`. */
export function itemLabel(key: string, decks: Deck[] = []): { title: string; where: string } {
  const p = parseItemKey(key)
  if (!p) return { title: key, where: '' }
  switch (p.kind) {
    case 'letter':
      return { title: `The letter ${p.id.toUpperCase()}`, where: 'Letter games' }
    case 'sound': {
      const ph = PHONEME_BY_ID[p.id]
      // A sound shared by several spellings says so, since a letter tile shows it too.
      const spelled = Object.keys(GRAPHEMES).filter((g) => GRAPHEMES[g] === p.id)
      const also = spelled.length > 1 ? `, spelled ${spelled.join(', ')}` : ''
      return { title: ph ? `The sound "${ph.say}", as in ${ph.as}${also}` : `The sound "${p.id}"${also}`, where: 'Blending' }
    }
    case 'word':
      return { title: `"${p.id}"`, where: 'Reading words' }
    case 'sight':
      return { title: `"${p.id}" on sight`, where: 'Sight words' }
    case 'fact':
      return { title: p.id, where: 'Facts' }
    case 'card': {
      const [deckId, cardId] = p.id.split('/')
      const deck = decks.find((d) => d.id === deckId)
      const card = deck?.cards.find((c) => c.id === cardId)
      const face = card?.front
      const title = face?.text ?? face?.emoji ?? face?.say ?? cardId ?? p.id
      return { title: card?.kind === 'generator' ? genLabel(card) : `"${title}"`, where: deck ? `${deck.title} deck` : 'Cards' }
    }
  }
}

function genLabel(card: Card): string {
  const max = card.gen?.params?.max
  const up = typeof max === 'number' ? ` within ${max}` : ''
  switch (card.gen?.type) {
    case 'pattern': return 'Finish the pattern'
    case 'add': return `Adding${up}`
    case 'subtract': return `Taking away${up}`
    case 'count': return `Counting${up}`
    case 'after': return 'What comes after'
    case 'before': return 'What comes before'
    case 'rhyme': return 'Rhymes'
    default: return card.front.text ?? card.id
  }
}

/** One item's memory, told plainly: how often, how well, when it comes back. */
export function itemFacts(m: ItemMemory | undefined, now: number): { standing: Standing; seen: number; right: number; streak: number; lastAt?: number; nextAt?: number; holdsDays?: number; ms?: number } {
  if (!m || !m.reps) return { standing: 'new', seen: 0, right: 0, streak: 0 }
  return {
    standing: standing(m, now),
    seen: m.seen, right: m.right, streak: m.streak,
    lastAt: m.last, nextAt: m.due,
    holdsDays: Math.max(1, Math.round(m.stability)),
    ms: m.ms,
  }
}

/** "today", "tomorrow", "in 3 days", "yesterday", "4 days ago", in her time zone. */
export function dayWords(at: number, now: number, tz: string): string {
  const n = diffDays(toCalendarDate(now, tz), toCalendarDate(at, tz))
  if (n === 0) return 'today'
  if (n === 1) return 'tomorrow'
  if (n === -1) return 'yesterday'
  return n > 0 ? `in ${n} days` : `${-n} days ago`
}

/**
 * A moment as a person in her home reads it: "today 1:34pm, 50 min ago",
 * "yesterday 5:10pm", "3 days ago 9:02am", "tomorrow 9:00am". Her calendar
 * day and wall clock, never UTC, so nothing minutes old reads as yesterday.
 */
export function whenWords(at: number, now: number, tz: string): string {
  let clock: string
  try {
    clock = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(at).replace(/\s/g, '').toLowerCase()
  } catch {
    return whenWords(at, now, 'UTC')
  }
  const mins = Math.round((now - at) / 60_000)
  const ago = mins < 0 || mins >= 6 * 60 ? '' : mins < 1 ? ', just now' : mins < 60 ? `, ${mins} min ago` : `, ${Math.floor(mins / 60)}h ${mins % 60}m ago`
  return `${dayWords(at, now, tz)} ${clock}${ago}`
}

// ─────────────────────────────────────────────────────────────
// Pictures and new decks
// ─────────────────────────────────────────────────────────────

/** A word-bank clay picture as an image source, or null for a word that has none. */
export function pictureSrc(word: string | undefined): string | null {
  const webp = word ? PICTURES[word.toLowerCase()] : undefined
  return webp ? `data:image/webp;base64,${webp}` : null
}

/** A deck's cover: a word-bank picture, an emoji, or a few characters ("ABC", "123"); all null means use its title's first letter. */
export function deckCover(deck: Pick<Deck, 'cover'> | null | undefined): { picture: string | null; emoji: string | null; text: string | null } {
  const c = deck?.cover?.trim() ?? ''
  const picture = pictureSrc(c)
  const emoji = !picture && /\p{Extended_Pictographic}/u.test(c) ? c : null
  const text = !picture && !emoji && c && [...c].length <= 3 ? c : null
  return { picture, emoji, text }
}

const DECK_HUES = ['literacy', 'math', 'creative', 'science', 'secondLanguage', 'grossMotor', 'time', 'fineMotor']

/** The domain hue a deck wears, picked by its id so it looks the same on every screen and in both apps. */
export function deckHueKey(id: string | undefined): string {
  let h = 0
  for (const ch of id ?? '') h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return DECK_HUES[h % DECK_HUES.length]!
}

export interface DeckSuggestion {
  /** What the chip says. */
  label: string
  /** The sentence it puts in the direction field. */
  direction: string
  /** A starter deck this matches, so it can be added without writing one. */
  starter?: string
}

const LANGUAGES = ['Spanish', 'Hebrew', 'Russian', 'Ukrainian', 'French', 'German', 'Italian', 'Portuguese', 'Armenian', 'Mandarin', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Polish', 'Greek', 'Dutch', 'Turkish', 'Farsi', 'Persian', 'Georgian', 'Yiddish', 'Swedish', 'Vietnamese', 'Tagalog']

/**
 * What to start next, in the order the method climbs: capitals, then
 * lowercase the day after, numbers, sight words, first words once letters
 * are coming, math; then a family language and decks about what she loves.
 * Decks she already has are left out by their id or their direction.
 */
export function deckSuggestions(input: { decks: Deck[]; interests?: string[]; about?: string[]; memories?: Record<string, ItemMemory>; now?: number }): DeckSuggestion[] {
  const have = new Set(input.decks.map((d) => d.id))
  const said = input.decks.map((d) => `${d.title} ${d.direction}`.toLowerCase())
  const has = (id: string, ...words: string[]) => have.has(id) || said.some((s) => words.some((w) => s.includes(w)))
  const now = input.now ?? Date.now()
  // Letters she holds, however her letter decks were named or made.
  const lettersKnown = input.memories
    ? knowledgeGrid(input.memories, input.decks, now).letters.filter((t) => RANK[t.standing] >= RANK.known).length
    : 0
  const out: DeckSuggestion[] = []
  const rung = (id: string, ready = true) => {
    const d = starterDeck(id)
    if (d && ready && !has(id, d.title.toLowerCase())) out.push({ label: d.title, direction: d.direction, starter: id })
  }
  // Lowercase comes the day after capitals, so it waits for capitals to exist.
  if (!has('capitals', 'capital')) rung('capitals')
  else rung('lowercase')
  rung('numbers')
  rung('sight-words')
  rung('sounds')
  rung('first-words', lettersKnown >= 8)
  rung('math')
  rung('patterns')
  // The next few rungs, then what is personal to her: a family language and what she loves.
  out.splice(4)
  const text = [...(input.about ?? []), ...(input.interests ?? [])].join(' ')
  for (const lang of LANGUAGES) {
    if (new RegExp(`\\b${lang}\\b`, 'i').test(text) && !said.some((s) => s.includes(lang.toLowerCase()))) {
      out.push({ label: `${lang} words`, direction: `Everyday ${lang} words for things she loves` })
    }
  }
  for (const interest of (input.interests ?? []).slice(0, 3)) {
    const topic = interest.replace(/^(she\s+)?(loves|likes|is into|is obsessed with|enjoys)\s+/i, '').replace(/[.!]+$/, '').trim()
    const Topic = topic ? topic[0]!.toUpperCase() + topic.slice(1) : ''
    if (topic && topic.length <= 60 && !said.some((s) => s.includes(topic.toLowerCase()))) {
      out.push({ label: Topic.length > 26 ? `${Topic.slice(0, 24)}…` : Topic, direction: `${Topic}: cards she will think are cool` })
    }
  }
  return out.slice(0, 7)
}

/**
 * How big a card face's text can be, as a fraction of its tile's width, so
 * its longest word stays on one line and the whole of it fits the tile.
 * Words shrink rather than break: AURORA split as AURO/RA is no longer her
 * name. Widths are rough per-letter estimates in ems, for any script:
 * capitals and digits wide, lowercase letters (Latin, Cyrillic, Hebrew,
 * Greek alike) medium, everything else narrow.
 */
export function textFit(text: string): number {
  const em = (w: string) => [...w].reduce((n, ch) => n + (/[\p{Lu}\p{Nd}]/u.test(ch) ? 0.72 : /\p{L}/u.test(ch) ? 0.6 : 0.34), 0)
  const longest = Math.max(1, ...text.split(/\s+/).map(em))
  // At size s the text takes about all*s/width lines of 1.1*s each, and a 3:4
  // tile is 4/3 of its width tall; ragged word wrap in a tile this narrow
  // wastes about half of each line, hence 0.6 rather than 1.2.
  return Math.min(0.9 / longest, Math.sqrt(0.6 / em(text)))
}
